/**
 * Battery Monitor - Phase 3 Implementation
 *
 * Listens to battery status changes and adapts virtual background quality.
 * Downgrades to VERY_LOW tier when battery < 20% and discharging.
 * Supports Web, Electron, Mobile browsers (with Battery Status API).
 *
 * Battery Status API: https://developer.mozilla.org/en-US/docs/Web/API/Battery_Status_API
 * Note: Deprecated but still supported on mobile, no replacement yet
 */

import logger from '../../virtual-background/logger';

import { DEVICE_CAPABILITY, getQualitySettings } from './deviceCapabilities';

/**
 * BatteryMonitor - Adapts quality based on battery level
 */
export default class BatteryMonitor {
    /**
     * Constructor for BatteryMonitor
     *
     * @param {Object} options - Configuration options
     */
    constructor(options = {}) {
        this._criticalBatteryLevel = options.criticalBatteryLevel || 0.2; // 20%
        this._batteryStatusApi = null;
        this._isMonitoring = false;
        this._lastBatteryLevel = null;
        this._lastCharging = null;
        this._currentEffectiveCapability = null;

        // Callback for when battery adaptation needed
        this._onBatteryAdapt = options.onBatteryAdapt || (() => {
            // noop
        });

        logger.debug('[BatteryMonitor] Initialized, critical level:', this._criticalBatteryLevel);
    }

    /**
     * Start monitoring battery status
     *
     * @returns {void}
     */
    async startMonitoring() {
        if (this._isMonitoring) {
            logger.debug('[BatteryMonitor] Already monitoring');

            return;
        }

        // Try to get Battery Status API
        try {
            // Modern API (if available in future)
            if ('getBattery' in navigator) {
                this._batteryStatusApi = await navigator.getBattery();
            }

            if (!this._batteryStatusApi) {
                logger.debug('[BatteryMonitor] Battery Status API not available on this platform');

                return;
            }

            this._isMonitoring = true;

            // Store initial state
            this._lastBatteryLevel = this._batteryStatusApi.level;
            this._lastCharging = this._batteryStatusApi.charging;

            logger.info('[BatteryMonitor] Started monitoring', {
                level: `${(this._lastBatteryLevel * 100).toFixed(1)}%`,
                charging: this._lastCharging,
                chargingTime: this._batteryStatusApi.chargingTime,
                dischargingTime: this._batteryStatusApi.dischargingTime
            });

            // Listen to battery events
            this._batteryStatusApi.addEventListener('levelchange', () => this._onBatteryChange());
            this._batteryStatusApi.addEventListener('chargingchange', () => this._onBatteryChange());
            this._batteryStatusApi.addEventListener('chargingtimechange', () => this._onBatteryChange());
            this._batteryStatusApi.addEventListener('dischargingtimechange', () => this._onBatteryChange());

            // Check initial state
            this._onBatteryChange();
        } catch (error) {
            logger.warn('[BatteryMonitor] Failed to initialize battery monitoring:', error);
            this._batteryStatusApi = null;
            this._isMonitoring = false;
        }
    }

    /**
     * Handle battery status change
     *
     * @private
     * @returns {void}
     */
    _onBatteryChange() {
        if (!this._batteryStatusApi) {
            return;
        }

        try {
            const level = this._batteryStatusApi.level;
            const charging = this._batteryStatusApi.charging;

            const levelPercent = (level * 100).toFixed(1);
            const chargingStatus = charging ? 'charging' : 'discharging';

            logger.debug('[BatteryMonitor] Battery changed:', {
                level: `${levelPercent}%`,
                charging: chargingStatus
            });

            // Check if we need to adapt
            const isCritical = level < this._criticalBatteryLevel && !charging;
            const wasCritical = this._lastBatteryLevel !== null
                && this._lastBatteryLevel < this._criticalBatteryLevel
                && !this._lastCharging;

            this._lastBatteryLevel = level;
            this._lastCharging = charging;

            // Adapt if crossing threshold
            if (isCritical && !wasCritical) {
                this._adaptForLowBattery();
            } else if (!isCritical && wasCritical) {
                this._restoreNormalBattery();
            }
        } catch (error) {
            logger.error('[BatteryMonitor] Error processing battery change:', error);
        }
    }

    /**
     * Adapt quality for low battery
     *
     * @private
     * @returns {void}
     */
    _adaptForLowBattery() {
        logger.warn('[BatteryMonitor] LOW BATTERY DETECTED - Adapting for battery conservation');

        // Downgrade to VERY_LOW tier for battery savings
        const qualitySettings = getQualitySettings(DEVICE_CAPABILITY.VERY_LOW);

        logger.info('[BatteryMonitor] Applying low-battery settings', {
            targetFps: qualitySettings.targetFps || 'N/A',
            renderingScale: qualitySettings.renderingScale,
            useGuidedFilter: qualitySettings.useGuidedFilter,
            temporalAlpha: qualitySettings.temporalAlpha
        });

        this._currentEffectiveCapability = DEVICE_CAPABILITY.VERY_LOW;

        this._onBatteryAdapt({
            adaptation: 'low-battery',
            capability: DEVICE_CAPABILITY.VERY_LOW,
            qualitySettings,
            reason: `Battery < ${(this._criticalBatteryLevel * 100).toFixed(0)}% and discharging`
        });
    }

    /**
     * Restore normal quality when battery recovers
     *
     * @private
     * @returns {void}
     */
    _restoreNormalBattery() {
        logger.info('[BatteryMonitor] Battery recovered - Restoring normal quality settings');

        // Notify to restore to actual device capability
        this._currentEffectiveCapability = null;

        this._onBatteryAdapt({
            adaptation: 'restored',
            capability: null,
            qualitySettings: null,
            reason: 'Battery level recovered, restoring normal operation'
        });
    }

    /**
     * Get current battery state
     *
     * @returns {Object} Battery state
     */
    getState() {
        if (!this._batteryStatusApi) {
            return {
                isMonitoring: false,
                available: false
            };
        }

        return {
            isMonitoring: this._isMonitoring,
            available: true,
            level: this._lastBatteryLevel,
            charging: this._lastCharging,
            levelPercent: `${(this._lastBatteryLevel * 100).toFixed(1)}%`,
            isCritical: this._lastBatteryLevel < this._criticalBatteryLevel && !this._lastCharging,
            currentEffectiveCapability: this._currentEffectiveCapability,
            chargingTime: this._batteryStatusApi.chargingTime,
            dischargingTime: this._batteryStatusApi.dischargingTime
        };
    }

    /**
     * Stop monitoring battery
     *
     * @returns {void}
     */
    stopMonitoring() {
        if (!this._isMonitoring || !this._batteryStatusApi) {
            return;
        }

        this._isMonitoring = false;

        try {
            this._batteryStatusApi.removeEventListener('levelchange', () => this._onBatteryChange());
            this._batteryStatusApi.removeEventListener('chargingchange', () => this._onBatteryChange());
            this._batteryStatusApi.removeEventListener('chargingtimechange', () => this._onBatteryChange());
            this._batteryStatusApi.removeEventListener('dischargingtimechange', () => this._onBatteryChange());
        } catch (error) {
            logger.debug('[BatteryMonitor] Error removing event listeners:', error);
        }

        logger.info('[BatteryMonitor] Stopped monitoring');
    }

    /**
     * Dispose resources
     *
     * @returns {void}
     */
    dispose() {
        this.stopMonitoring();
        this._batteryStatusApi = null;
    }
}
